/**
 * Boot Error Screen Component
 *
 * Shown when the app fails to boot (Firebase timeout, network error, etc.)
 * Provides a retry button to attempt boot again.
 */

import React, { useState } from 'react';
import { Box, Typography, Button, CircularProgress, useTheme } from '@mui/material';
import { Refresh, WifiOff } from '@mui/icons-material';
import { font } from '../../utils/fontHelper';

interface BootErrorScreenProps {
  onRetry: () => Promise<void>;
  error?: Error | null;
}

const BootErrorScreen: React.FC<BootErrorScreenProps> = ({ onRetry, error }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await onRetry();
    } finally {
      setIsRetrying(false);
    }
  };

  // Determine error type for messaging
  const isNetworkError = error?.message?.toLowerCase().includes('network') ||
    error?.message?.toLowerCase().includes('timeout') ||
    error?.message?.toLowerCase().includes('offline');

  return (
    <Box
      data-testid="boot-error-screen"
      sx={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: isDark
          ? 'linear-gradient(180deg, #1C1C1E 0%, #121212 100%)'
          : 'linear-gradient(180deg, #FFFFFF 0%, #F5F5F5 100%)',
        zIndex: 9999,
        padding: 3,
      }}
    >
      {/* Error Icon */}
      <Box
        sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: isDark
            ? 'rgba(255, 82, 82, 0.15)'
            : 'rgba(255, 82, 82, 0.1)',
          mb: 3,
        }}
      >
        {isNetworkError ? (
          <WifiOff
            sx={{
              fontSize: 40,
              color: '#FF5252',
            }}
          />
        ) : (
          <Typography
            sx={{
              fontSize: 40,
            }}
          >
            :(
          </Typography>
        )}
      </Box>

      {/* Error Title */}
      <Typography
        sx={{
          fontFamily: font(),
          fontWeight: 700,
          fontSize: 22,
          color: isDark ? '#FFFFFF' : '#1C1C1E',
          mb: 1.5,
          textAlign: 'center',
        }}
      >
        {isNetworkError ? 'Connection Problem' : 'Something Went Wrong'}
      </Typography>

      {/* Error Message */}
      <Typography
        sx={{
          fontFamily: font(),
          fontSize: 15,
          color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
          mb: 4,
          textAlign: 'center',
          maxWidth: 280,
          lineHeight: 1.5,
        }}
      >
        {isNetworkError
          ? "We couldn't connect to our servers. Please check your internet connection and try again."
          : "We had trouble starting the app. This might be a temporary issue."}
      </Typography>

      {/* Retry Button */}
      <Button
        data-testid="boot-error-retry"
        variant="contained"
        onClick={handleRetry}
        disabled={isRetrying}
        startIcon={isRetrying ? <CircularProgress size={18} color="inherit" /> : <Refresh />}
        sx={{
          fontFamily: font(),
          fontWeight: 600,
          fontSize: 15,
          textTransform: 'none',
          borderRadius: 3,
          px: 4,
          py: 1.5,
          background: '#1976d2',
          '&:hover': {
            background: '#1565c0',
          },
          '&:disabled': {
            background: isDark ? 'rgba(25, 118, 210, 0.5)' : 'rgba(25, 118, 210, 0.7)',
            color: 'rgba(255, 255, 255, 0.7)',
          },
        }}
      >
        {isRetrying ? 'Retrying...' : 'Try Again'}
      </Button>

      {/* Debug Info (only in development) */}
      {process.env.NODE_ENV === 'development' && error && (
        <Typography
          sx={{
            fontFamily: 'monospace',
            fontSize: 11,
            color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
            mt: 4,
            maxWidth: 300,
            textAlign: 'center',
            wordBreak: 'break-word',
          }}
        >
          {error.message}
        </Typography>
      )}
    </Box>
  );
};

export default BootErrorScreen;
